/** Where the school's Chromebooks are: the map, what's reporting, and what to do about one that has gone missing. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Battery, MapPin, Radio, Settings, ShieldAlert, Siren, Wifi } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FleetMap } from '../../components/fleet-map';
import { AddOnRequired } from '../../components/plan';
import { useToast } from '../../components/toast';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Field, Input, Modal, PageHeader, PageLoader, Select, StatCard, Textarea } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePlan } from '../../lib/billing';
import {
  accuracyNote,
  ALERT_LABEL,
  DARK_REASON_LABEL,
  useDeviceHistory,
  useFleet,
  useGeofences,
  useLocationHealth,
  type LocatedDevice,
} from '../../lib/device-location';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useBuildings } from '../../lib/hooks';

export function FleetPage() {
  const plan = usePlan();
  const { can } = useAuth();
  const navigate = useNavigate();
  const allowed = plan.has('DEVICE_LOCATION');
  const [filters, setFilters] = useState({ search: '', buildingId: '', status: '', missing: '' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      buildingId: filters.buildingId || undefined,
      status: filters.status || undefined,
      missing: filters.missing || undefined,
    }),
    [filters],
  );
  const fleet = useFleet(query, allowed);
  const health = useLocationHealth(allowed);
  const fences = useGeofences(allowed);
  const buildings = useBuildings();

  if (!allowed) {
    return (
      <AddOnRequired
        feature="DEVICE_LOCATION"
        description="See where the school's Chromebooks are, place them indoors from your access points, draw the campus and hear when a device leaves it, and put a device into missing mode until it's found."
      />
    );
  }
  if (fleet.isLoading) return <PageLoader />;
  if (fleet.error || !fleet.data) return <ErrorState error={fleet.error} onRetry={() => fleet.refetch()} />;

  const devices = fleet.data.items;
  const missing = devices.filter((device) => device.missingAt && !device.recoveredAt);

  return (
    <>
      <PageHeader
        title="Chromebook location"
        description="Where the school's Chromebooks last reported from, and what to do when one goes missing."
        actions={
          <>
            <Button icon={<Siren />} variant={missing.length ? 'danger' : 'secondary'} onClick={() => setFilters((f) => ({ ...f, missing: missing.length ? 'true' : '' }))}>
              {missing.length ? `${missing.length} missing` : 'None missing'}
            </Button>
            {can('ADMIN') && (
              <Button icon={<Settings />} onClick={() => navigate('/device-location/settings')}>
                Settings
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-6">
        {health.data && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Reporting" value={health.data.reporting} sub={`${health.data.coverage}% of ${health.data.chromebooks} Chromebooks`} icon={<Radio />} />
            <StatCard label="Quiet" value={health.data.quiet} sub="Reported before, not lately" icon={<Wifi />} tone="amber" />
            <StatCard label="Off campus now" value={health.data.offCampusNow} sub="Outside the school's areas" icon={<MapPin />} tone={health.data.offCampusNow ? 'red' : 'brand'} />
            <StatCard
              label="Missing"
              value={health.data.missing}
              sub={health.data.medianHoursToRecover !== null ? `Usually found in ${health.data.medianHoursToRecover} h` : 'None flagged yet'}
              icon={<ShieldAlert />}
              tone={health.data.missing ? 'red' : 'brand'}
            />
          </div>
        )}

        <Card>
          <CardHeader title="Fleet map" description={`${devices.length} Chromebook${devices.length === 1 ? '' : 's'}, newest reports first.`} />
          <div className="space-y-4 p-5">
            <div className="flex flex-wrap gap-3">
              <Input
                className="w-64"
                placeholder="Search tag, serial, or who has it"
                value={filters.search}
                onChange={(event) => setFilters((f) => ({ ...f, search: event.target.value }))}
              />
              <Select value={filters.buildingId} onChange={(event) => setFilters((f) => ({ ...f, buildingId: event.target.value }))}>
                <option value="">All buildings</option>
                {(buildings.data ?? []).map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </Select>
              <Select value={filters.status} onChange={(event) => setFilters((f) => ({ ...f, status: event.target.value }))}>
                <option value="">Reporting or not</option>
                <option value="REPORTING">Reporting</option>
                <option value="QUIET">Quiet</option>
                <option value="DARK">Never reported</option>
              </Select>
              <Select value={filters.missing} onChange={(event) => setFilters((f) => ({ ...f, missing: event.target.value }))}>
                <option value="">Missing or not</option>
                <option value="true">Flagged missing</option>
                <option value="false">Not missing</option>
              </Select>
            </div>
            <FleetMap devices={devices} geofences={fences.data ?? []} tiles={fleet.data.map} selectedId={selectedId} onSelect={setSelectedId} />
            {!fleet.data.map.tileUrl && (
              <p className="text-xs text-slate-500">
                Devices and areas plot on a plain backdrop - a street map isn't turned on for this Edvora yet.
              </p>
            )}
            {devices.every((device) => device.lastLatitude === null) && (
              <Alert tone="blue" title="Nothing on the map yet">
                Chromebooks appear here once the extension is installed from Google Admin and the school's enrollment token is in its policy.
                {can('ADMIN') && (
                  <>
                    {' '}
                    <Link to="/device-location/settings" className="font-medium underline">
                      Set it up in settings
                    </Link>
                    .
                  </>
                )}
              </Alert>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Chromebooks" description="Pick one to see where it has been, or to flag it missing." />
          {devices.length === 0 ? (
            <EmptyState icon={<MapPin />} title="No Chromebooks match" description="Try a different filter." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {devices.map((device) => (
                <li key={device.id}>
                  <button type="button" className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-left hover:bg-slate-50" onClick={() => setSelectedId(device.id)}>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-slate-900">{device.asset?.assetTag ?? device.serialNumber}</span>
                      <span className="block text-sm text-slate-500">
                        {[device.asset?.name, device.room?.name ?? device.building?.name, device.lastUserEmail].filter(Boolean).join(' · ') || device.serialNumber}
                      </span>
                    </span>
                    <DeviceState device={device} />
                    <span className="w-40 text-right text-sm text-slate-500">{device.lastSeenAt ? formatRelative(device.lastSeenAt) : 'Never'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <DevicePanel deviceId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}

function DeviceState({ device }: { device: LocatedDevice }) {
  if (device.missingAt && !device.recoveredAt) return <Badge tone="red">Missing</Badge>;
  if (device.status === 'REPORTING') return <Badge tone="green">Reporting</Badge>;
  const reason = device.darkReason ? DARK_REASON_LABEL[device.darkReason] : 'Not reporting';
  return <Badge tone={device.status === 'QUIET' ? 'amber' : 'slate'}>{reason}</Badge>;
}

/** One Chromebook: where it has been, what it's on, and missing mode. */
function DevicePanel({ deviceId, onClose }: { deviceId: string | null; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const history = useDeviceHistory(deviceId);
  const [note, setNote] = useState('');
  const [lockMessage, setLockMessage] = useState('');

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['device-location'] });
  };
  const flag = useMutation({
    mutationFn: () => api.post(`/device-location/devices/${deviceId}/missing`, { note: note || undefined, lockMessage: lockMessage || undefined }),
    onSuccess: () => {
      refresh();
      toast.success('Flagged missing. It will report every few minutes until it’s found.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const found = useMutation({
    mutationFn: () => api.delete(`/device-location/devices/${deviceId}/missing`),
    onSuccess: () => {
      refresh();
      toast.success('Marked found.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const device = history.data?.device;
  const missing = !!device?.missingAt && !device.recoveredAt;

  return (
    <Modal
      open={!!deviceId}
      onClose={onClose}
      size="xl"
      title={device ? (device.asset?.assetTag ?? device.serialNumber) : 'Chromebook'}
      description={device ? [device.asset?.name, device.serialNumber, device.orgUnitPath].filter(Boolean).join(' · ') : undefined}
      footer={
        device && (
          <>
            <a href={`/api/device-location/devices/${device.id}/recovery`} target="_blank" rel="noopener noreferrer">
              <Button>Recovery packet</Button>
            </a>
            {missing ? (
              <Button variant="primary" loading={found.isPending} onClick={() => found.mutate()}>
                Mark found
              </Button>
            ) : (
              <Button variant="danger" icon={<Siren />} loading={flag.isPending} onClick={() => flag.mutate()}>
                Flag missing
              </Button>
            )}
          </>
        )
      }
    >
      {history.isLoading || !device ? (
        <PageLoader />
      ) : (
        <div className="space-y-5">
          {missing && (
            <Alert tone="red" title={`Flagged missing by ${device.missingByName ?? 'a technician'}`}>
              {device.missingNote || 'No note.'} It reports every few minutes and shows the school’s message until it’s found.
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <div className="space-y-1 p-4 text-sm">
                <p className="font-medium text-slate-900">Last report</p>
                <p className="text-slate-600">{device.lastSeenAt ? formatDateTime(device.lastSeenAt) : 'Never'}</p>
                <p className="text-slate-500">{accuracyNote(device.lastAccuracyM)}</p>
                <p className="text-slate-500">{[device.room?.name, device.building?.name].filter(Boolean).join(' · ') || 'No room worked out'}</p>
              </div>
            </Card>
            <Card>
              <div className="space-y-1 p-4 text-sm">
                <p className="font-medium text-slate-900">On the day</p>
                <p className="text-slate-600">{device.lastUserEmail ?? 'No one signed in'}</p>
                <p className="text-slate-500">{device.lastNetworkName ? `Wi-Fi: ${device.lastNetworkName}` : 'Network unknown'}</p>
                <p className="flex items-center gap-1 text-slate-500">
                  <Battery className="size-4" /> {device.batteryPercent === null ? 'Battery unknown' : `${device.batteryPercent}%${device.charging ? ' and charging' : ''}`}
                </p>
              </div>
            </Card>
          </div>

          {!missing && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Note for the record">
                <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Not returned after the trip" />
              </Field>
              <Field label="Message to show on the device">
                <Textarea rows={2} value={lockMessage} onChange={(event) => setLockMessage(event.target.value)} placeholder="This Chromebook belongs to… Please call…" />
              </Field>
            </div>
          )}

          {history.data!.alerts.length > 0 && (
            <Card>
              <CardHeader title="Alerts" />
              <ul className="divide-y divide-slate-100 text-sm">
                {history.data!.alerts.map((alert) => (
                  <li key={alert.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                    <span>
                      <Badge tone={alert.clearedAt ? 'slate' : 'red'}>{ALERT_LABEL[alert.kind]}</Badge> <span className="text-slate-600">{alert.summary}</span>
                    </span>
                    <span className="text-slate-500">{formatRelative(alert.firedAt)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card>
            <CardHeader title="Where it has been" description="Newest first, over the last two weeks." />
            {history.data!.points.length === 0 ? (
              <EmptyState icon={<MapPin />} title="No reports yet" description="It hasn’t checked in during this period." />
            ) : (
              <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto text-sm">
                {history.data!.points.map((point) => (
                  <li key={point.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                    <span className="text-slate-700">
                      {[point.room?.name, point.building?.name].filter(Boolean).join(' · ') ||
                        (point.latitude !== null ? `${point.latitude.toFixed(5)}, ${point.longitude!.toFixed(5)}` : 'Unknown')}
                      {point.placedBy === 'ACCESS_POINT' && <span className="ml-2 text-xs text-slate-400">from {point.networkName ?? 'Wi-Fi'}</span>}
                    </span>
                    <span className="text-slate-500">
                      {accuracyNote(point.accuracyM)} · {formatDateTime(point.occurredAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </Modal>
  );
}
